import { useState, useCallback } from 'react';
import { ethers } from 'ethers';
import KutroTokenABI from '@/config/KutroTokenABI.json';
import KutroStakingABI from '@/config/KutroStakingABI.json';
import { sendAttributed } from '@/lib/erc8021';

// Contract addresses - Update after deployment
const CONTRACTS = {
  84532: { // Base Sepolia
    token: process.env.REACT_APP_KOJIRU_TOKEN_ADDRESS || '',
    staking: process.env.REACT_APP_KOJIRU_STAKING_ADDRESS || ''
  },
  8453: { // Base Mainnet (V2 contracts)
    token: process.env.REACT_APP_KOJIRU_TOKEN_ADDRESS || '0xb50007B292256E2e54253D2110013465f9441482',
    staking: process.env.REACT_APP_KOJIRU_STAKING_ADDRESS || '0xaae9ae4d52fe13384522e0ef4114b0a5f5d62d68',
    subscription: process.env.REACT_APP_KOJIRU_SUBSCRIPTION_ADDRESS || '0x52fcA0998076b599c932F1647137DCBEb78FA839',
    usdc: process.env.REACT_APP_USDC_BASE_ADDRESS || '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913'
  }
};

export function useKutroContracts() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getProvider = useCallback(() => {
    if (!window.ethereum) {
      throw new Error('MetaMask not installed');
    }
    return new ethers.BrowserProvider(window.ethereum);
  }, []);

  const getContracts = useCallback(async () => {
    const provider = getProvider();
    const network = await provider.getNetwork();
    const chainId = Number(network.chainId);
    
    const addresses = CONTRACTS[chainId];
    if (!addresses || !addresses.token || !addresses.staking) {
      throw new Error(`Contracts not deployed on chain ${chainId}. Please switch to Base.`);
    }

    const signer = await provider.getSigner();
    
    const tokenContract = new ethers.Contract(addresses.token, KutroTokenABI, signer);
    const stakingContract = new ethers.Contract(addresses.staking, KutroStakingABI, signer);

    return { tokenContract, stakingContract, signer, chainId };
  }, [getProvider]);

  // Get token balance
  const getTokenBalance = useCallback(async (address) => {
    try {
      const { tokenContract } = await getContracts();
      const balance = await tokenContract.balanceOf(address);
      return ethers.formatEther(balance);
    } catch (err) {
      console.error('Error getting token balance:', err);
      throw err;
    }
  }, [getContracts]);

  // Get staking info
  const getStakeInfo = useCallback(async (address) => {
    try {
      const { stakingContract } = await getContracts();
      const [amount, startTime, pendingRewards, tier, tierName] = await stakingContract.getStakeInfo(address);
      
      return {
        stakedAmount: ethers.formatEther(amount),
        startTime: Number(startTime),
        pendingRewards: ethers.formatEther(pendingRewards),
        tier: Number(tier),
        tierName
      };
    } catch (err) {
      console.error('Error getting stake info:', err);
      throw err;
    }
  }, [getContracts]);

  // Get pool stats
  const getPoolStats = useCallback(async () => {
    try {
      const { stakingContract } = await getContracts();
      const [totalStaked, rewardPool, totalRewardsDistributed, rewardRate, stakerCount] = await stakingContract.getPoolStats();
      
      return {
        totalStaked: ethers.formatEther(totalStaked),
        rewardPool: ethers.formatEther(rewardPool),
        totalRewardsDistributed: ethers.formatEther(totalRewardsDistributed),
        rewardRate: Number(rewardRate) / 100, // Convert basis points to percentage
        stakerCount: Number(stakerCount)
      };
    } catch (err) {
      console.error('Error getting pool stats:', err);
      throw err;
    }
  }, [getContracts]);

  // Approve tokens for staking
  const approveTokens = useCallback(async (amount) => {
    setLoading(true);
    setError(null);
    try {
      const { tokenContract, stakingContract } = await getContracts();
      const stakingAddress = await stakingContract.getAddress();
      const amountWei = ethers.parseEther(amount.toString());
      
      const tx = await sendAttributed(tokenContract, 'approve', [stakingAddress, amountWei]);
      await tx.wait();
      
      return tx.hash;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getContracts]);

  // Stake tokens
  const stakeTokens = useCallback(async (amount) => {
    setLoading(true);
    setError(null);
    try {
      const { tokenContract, stakingContract, signer } = await getContracts();
      const stakingAddress = await stakingContract.getAddress();
      const amountWei = ethers.parseEther(amount.toString());
      const userAddress = await signer.getAddress();

      // Check allowance
      const allowance = await tokenContract.allowance(userAddress, stakingAddress);
      
      if (allowance < amountWei) {
        // Approve first
        const approveTx = await sendAttributed(tokenContract, 'approve', [stakingAddress, amountWei]);
        await approveTx.wait();
      }

      // Stake
      const tx = await sendAttributed(stakingContract, 'stake', [amountWei]);
      const receipt = await tx.wait();
      
      return {
        hash: tx.hash,
        receipt
      };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getContracts]);

  // Unstake tokens
  const unstakeTokens = useCallback(async (amount) => {
    setLoading(true);
    setError(null);
    try {
      const { stakingContract } = await getContracts();
      const amountWei = ethers.parseEther(amount.toString());

      const tx = await sendAttributed(stakingContract, 'unstake', [amountWei]);
      const receipt = await tx.wait();
      
      return {
        hash: tx.hash,
        receipt
      };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getContracts]);

  // Claim rewards
  const claimRewards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { stakingContract } = await getContracts();

      const tx = await sendAttributed(stakingContract, 'claimRewards', []);
      const receipt = await tx.wait();
      
      return {
        hash: tx.hash,
        receipt
      };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getContracts]);

  // Check if contracts are deployed
  const checkContractsDeployed = useCallback(async () => {
    try {
      const provider = getProvider();
      const network = await provider.getNetwork();
      const chainId = Number(network.chainId);
      
      const addresses = CONTRACTS[chainId];
      if (!addresses?.token || !addresses?.staking) {
        return { deployed: false, chainId, reason: 'Addresses not configured' };
      }

      // Check if contracts exist
      const tokenCode = await provider.getCode(addresses.token);
      const stakingCode = await provider.getCode(addresses.staking);

      if (tokenCode === '0x' || stakingCode === '0x') {
        return { deployed: false, chainId, reason: 'Contracts not deployed at configured addresses' };
      }

      return { deployed: true, chainId, addresses };
    } catch (err) {
      return { deployed: false, reason: err.message };
    }
  }, [getProvider]);

  return {
    loading,
    error,
    getTokenBalance,
    getStakeInfo,
    getPoolStats,
    approveTokens,
    stakeTokens,
    unstakeTokens,
    claimRewards,
    checkContractsDeployed
  };
}
